// client/src/components/AdminProfil.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { firestore, storage } from '../../firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { ref, getDownloadURL, listAll, uploadBytes } from 'firebase/storage';
import '../CSS/Dashboard.css';
import AdminNavbar from './AdminNavbar';
import { LanguageSelector } from '../../i18n';
import { useTranslation } from 'react-i18next';
import { FaUser, FaCamera, FaExternalLinkAlt, FaSignOutAlt } from 'react-icons/fa';
import AccountSwitcher from '../DIVERS/AccountSwitcher';
import { logoutSafely } from '../../utils/logoutSafely';

// ====== PARAMS COUTS ======
const PROFILE_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours

// Initialisation des Settings depuis le localStorage pour un affichage immédiat
const initialSettings = localStorage.getItem('Settings')
  ? JSON.parse(localStorage.getItem('Settings'))
  : { email: '', pseudo: '' };

const AdminProfil = () => {
  const { t } = useTranslation();

  // Etat auth minimal pour éviter getAuth() partout
  const [uid, setUid] = useState(null);

  // Settings (email/pseudo) — affichage instantané via cache
  const [user, setUser] = useState(initialSettings);
  const [newPseudo, setNewPseudo] = useState('');

  // Photo de profil (cache d’URL)
  const [profilePhotoURL, setProfilePhotoURL] = useState(localStorage.getItem('profilePhotoURL') || null);
  const fileInputRef = useRef(null);

  const [showModal, setShowModal] = useState(false);

  // ---------- AUTH LISTENER (une seule fois) ----------
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (current) => {
      setUid(current?.uid || null);
    });
    return () => unsub();
  }, []);

  // ---------- SETTINGS (Firestore) -----------
  // On ne lit le doc Firestore que si le cache ne contient pas encore email/pseudo
  useEffect(() => {
    const needFetch = !user?.email || !user?.pseudo;
    if (!uid || !needFetch) return;

    let abort = false;
    (async () => {
      try {
        const snap = await getDoc(doc(firestore, 'users', uid));
        if (abort) return;
        if (snap.exists()) {
          const data = snap.data();
          // Màj état + cache
          const merged = { email: data.email || '', pseudo: data.pseudo || '' };
          setUser((prev) => ({ ...prev, ...merged }));
          localStorage.setItem('Settings', JSON.stringify({ ...(JSON.parse(localStorage.getItem('Settings') || '{}')), ...merged }));
        }
      } catch (e) {
        console.error('Erreur lecture utilisateur (optimisé):', e);
      }
    })();
    return () => { abort = true; };
  }, [uid, user?.email, user?.pseudo]);

  // ---------- PHOTO : stratégie cache-first ----------
  // On n’appelle PAS listAll au montage si on a déjà une URL en cache récente.
  // On propose un bouton "Rafraîchir la photo" pour re-lire côté Storage si besoin.
  const isProfileCacheFresh = useCallback(() => {
    const ts = parseInt(localStorage.getItem('profilePhotoURL_TS') || '0', 10);
    if (!ts) return false;
    return Date.now() - ts < PROFILE_CACHE_TTL_MS;
  }, []);

  const setProfileCache = useCallback((url) => {
    if (!url) {
      localStorage.removeItem('profilePhotoURL');
      localStorage.removeItem('profilePhotoURL_TS');
      setProfilePhotoURL(null);
      return;
    }
    localStorage.setItem('profilePhotoURL', url);
    localStorage.setItem('profilePhotoURL_TS', String(Date.now()));
    setProfilePhotoURL(url);
  }, []);

  // Récupère la dernière photo UNIQUEMENT si pas de cache OU si on force un refresh.
  const fetchLatestProfilePhoto = useCallback(async (force = false) => {
    if (!uid) return;
    if (!force && profilePhotoURL && isProfileCacheFresh()) return; // cache valable → pas d’appel Storage

    try {
      // ATTENTION: listAll lit tout le dossier. On NE l’appelle que quand c’est nécessaire.
      const folderRef = ref(storage, `${uid}/photo-profil`);
      const { items } = await listAll(folderRef);
      if (!items?.length) {
        setProfileCache(null);
        return;
      }
      // On choisit le dernier par tri sur le nom (tu nommes déjà avec Date.now()_, parfait)
      const sorted = [...items].sort((a, b) => {
        const ta = parseInt(a.name.split('_')[0], 10);
        const tb = parseInt(b.name.split('_')[0], 10);
        return (tb || 0) - (ta || 0);
      });
      const latestRef = sorted[0];
      const url = await getDownloadURL(latestRef);
      setProfileCache(url);
    } catch (e) {
      console.error('Erreur fetchLatestProfilePhoto:', e);
      // on garde le cache existant si on en a un
    }
  }, [uid, profilePhotoURL, isProfileCacheFresh, setProfileCache]);

  // Montée: si pas d’URL en cache, on tente de charger une seule fois.
  useEffect(() => {
    if (!uid) return;
    if (!profilePhotoURL) {
      fetchLatestProfilePhoto(false);
    }
  }, [uid, profilePhotoURL, fetchLatestProfilePhoto]);

  // Upload économe : on n’utilise pas listAll, on prend l’URL du fichier qu’on vient d’uploader
  const handleProfilePhotoChange = useCallback(async (e) => {
    if (!uid) return;
    const file = e.target.files?.[0];
    if (!file) return;
    const fileRef = ref(storage, `${uid}/photo-profil/${Date.now()}_${file.name}`);
    try {
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef); // URL directe du fichier déposé
      setProfileCache(url); // maj cache local
    } catch (error) {
      console.error('Erreur lors du téléchargement de la photo de profil:', error);
    }
  }, [uid, setProfileCache]);

  // Sauvegarde pseudo (1 écriture)
  const handleSavePseudo = useCallback(async () => {
    if (!uid || !newPseudo.trim()) {
      alert("Veuillez entrer un nom d'entreprise valide.");
      return;
    }
    try {
      const userRef = doc(firestore, 'users', uid);
      await updateDoc(userRef, { pseudo: newPseudo });
      const updated = { ...user, pseudo: newPseudo };
      setUser(updated);
      // MAJ cache settings
      localStorage.setItem('Settings', JSON.stringify({ ...(JSON.parse(localStorage.getItem('Settings') || '{}')), ...updated }));
      setShowModal(false);
    } catch (e) {
      console.error('Erreur update pseudo:', e);
    }
  }, [uid, newPseudo, user]);

  // Déconnexion
  const handleLogout = useCallback(async () => {
    const auth = getAuth();
    try {
      await logoutSafely(auth);
    } catch (error) {
      console.error('Erreur lors de la déconnexion :', error?.message || error);
    } finally {
      try {
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userRole');
      } catch {}
      window.location.href = '/';
    }
  }, []);


  return (
    <div>
      <AdminNavbar />
      <div className="dashboard-container">
        <style>{`
          .adp-page { width: 100%; display: flex; flex-direction: column; gap: 20px; }
          .adp-page .eb-page-header { margin-bottom: 4px; }
          .dashboard-container .adp-page .eb-page-title { margin: 0; text-align: left; }
          .adp-identity { padding: 24px; display: flex; align-items: center; gap: 20px; }
          .adp-photo { position: relative; width: 88px; height: 88px; border-radius: 50%; flex-shrink: 0; cursor: pointer; background: var(--eb-surface-2); border: 1px solid var(--eb-border); display: flex; align-items: center; justify-content: center; color: var(--eb-subtle); transition: box-shadow .15s ease; }
          .adp-photo:hover { box-shadow: var(--eb-ring); }
          .adp-photo img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }
          .adp-photo__edit { position: absolute; right: -2px; bottom: -2px; width: 30px; height: 30px; border-radius: 50%; background: var(--eb-surface); border: 1px solid var(--eb-border-strong); color: var(--eb-text-2); display: flex; align-items: center; justify-content: center; box-shadow: var(--eb-shadow-sm); font-size: 12px; }
          .adp-identity__hint { margin: 4px 0 0; font-size: 13px; color: var(--eb-muted); }
          .adp-card { text-align: left; height: 100%; }
          .adp-page .eb-grid-2 { align-items: stretch; }
          .dashboard-container .adp-card .eb-card__title { text-align: left; }
          .adp-card .eb-card__body { padding: 4px 20px; }
          .adp-row { margin: 0; }
          .adp-row__label { font-size: 14px; font-weight: 600; color: var(--eb-text-2); }
          .adp-row__value { display: inline-flex; align-items: center; gap: 12px; flex-wrap: wrap; font-size: 14.5px; color: var(--eb-text); word-break: break-word; min-width: 0; }
          .adp-links { display: flex; flex-direction: column; }
          .adp-link { display: flex; align-items: center; justify-content: space-between; gap: 12px; width: 100%; min-height: 52px; padding: 0; background: none; border: 0; border-bottom: 1px solid var(--eb-border); color: var(--eb-text); font-size: 14.5px; font-weight: 500; text-align: left; cursor: pointer; }
          .adp-link:last-child { border-bottom: 0; }
          .adp-link:hover { color: var(--eb-brand); }
          .adp-link svg { color: var(--eb-subtle); flex-shrink: 0; }
          .adp-session { padding: 4px 20px; }
          .adp-session .logout-container { width: auto; margin: 0; }
          .adp-session__title { margin: 0; font-size: 14.5px; font-weight: 600; color: var(--eb-text); }
          .adp-session__hint { margin: 2px 0 0; font-size: 13px; color: var(--eb-muted); }

          .adp-modal-overlay { position: fixed; inset: 0; background: rgba(15,35,56,.55); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; padding: 16px; z-index: 1000; }
          .adp-modal { background: var(--eb-surface); border-radius: var(--eb-radius-xl); box-shadow: var(--eb-shadow-lg); padding: 24px; width: 100%; max-width: 420px; max-height: calc(100dvh - 32px); overflow-y: auto; display: flex; flex-direction: column; gap: 16px; }
          .dashboard-container .adp-modal__title { margin: 0; font-size: 19px; font-weight: 700; text-align: left; color: var(--eb-text); }
          .adp-modal__actions { display: flex; justify-content: center; gap: 10px; }

          #language-selector h3 { margin: 0 0 6px; font-size: 13px; font-weight: 500; color: var(--eb-muted); text-align: left; }
          #language-selector select {
            min-height: 40px; padding: 0 36px 0 12px; border-radius: var(--eb-radius-sm);
            border: 1px solid var(--eb-border-strong); background-color: var(--eb-surface); font-size: 14px;
            appearance: none; -webkit-appearance: none;
            background-image: linear-gradient(45deg, transparent 50%, var(--eb-subtle) 50%), linear-gradient(135deg, var(--eb-subtle) 50%, transparent 50%);
            background-position: calc(100% - 16px) 50%, calc(100% - 11px) 50%;
            background-size: 5px 5px, 5px 5px; background-repeat: no-repeat;
          }
          #language-selector button {
            min-height: 36px; padding: 0 12px; border-radius: var(--eb-radius-sm);
            border: 1px solid var(--eb-border-strong); background: var(--eb-surface);
            margin: 0 4px 0 0; font-size: 13.5px; font-weight: 600; color: var(--eb-text-2);
          }
          #language-selector button:hover { background: var(--eb-surface-2); }
          #language-selector .active, #language-selector .selected { color: var(--eb-brand); border-color: var(--eb-brand-300); background: var(--eb-brand-50); }

          @media (max-width: 480px) {
            .adp-identity { flex-direction: column; text-align: center; padding: 20px; gap: 14px; }
            .adp-card .eb-card__body, .adp-session { padding: 4px 16px; }
            .adp-session .logout-container, .adp-session .logout-button { width: 100%; justify-content: center; }
            .adp-modal__actions .eb-btn { width: 100%; }
          }
        `}</style>

        <div className="adp-page">
          <header className="eb-page-header">
            <div>
              <h1 className="eb-page-title">{t('Votre profil')}</h1>
              <p className="eb-page-subtitle">Compte administrateur et préférences.</p>
            </div>
          </header>

          {/* Identité */}
          <section className="eb-card adp-identity">
            {/* Photo de profil */}
            <div
              className="profile-photo-container adp-photo"
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              title="Changer la photo de profil"
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
            >
              {profilePhotoURL ? (
                <img src={profilePhotoURL} alt="Profil" className="profile-photo" />
              ) : (
                <FaUser size={36} aria-hidden="true" />
              )}
              <span className="adp-photo__edit" aria-hidden="true"><FaCamera /></span>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleProfilePhotoChange}
                accept="image/*"
              />
            </div>

            <div style={{ minWidth: 0 }}>
              <AccountSwitcher
                label={user?.pseudo || user?.email || 'Mon compte'}
                labelStyle={{ fontWeight: 700, fontSize: '1.15rem', color: 'var(--eb-text)' }}
              />
              <p className="adp-identity__hint">Cliquez sur la photo pour la modifier.</p>
            </div>
          </section>

          <div className="eb-grid-2">
            {/* Informations */}
            <section className="eb-card adp-card">
              <div className="eb-card__head">
                <h2 className="eb-card__title adp-card__title">Informations</h2>
              </div>
              <div className="eb-card__body user-info">
                <p className="eb-row adp-row">
                  <span className="adp-row__label">Email</span>
                  <span className="adp-row__value"><span>{user?.email}</span></span>
                </p>

                {/* Sélecteur de langue */}
                <div className="eb-row adp-row">
                  <span className="adp-row__label">Langue</span>
                  <div id="language-selector" className="adp-row__value">
                    <LanguageSelector />
                  </div>
                </div>
              </div>
            </section>

            {/* Documents */}
            <section className="eb-card adp-card">
              <div className="eb-card__head">
                <h2 className="eb-card__title adp-card__title">Documents</h2>
              </div>
              <div className="eb-card__body adp-links">
                <button className="adp-link" onClick={() => window.open('/legal/cgu', '_blank')}>
                  Conditions générales d'utilisation <FaExternalLinkAlt aria-hidden="true" />
                </button>
                <button className="adp-link" onClick={() => window.open('/legal/cgv', '_blank')}>
                  Conditions générales de vente <FaExternalLinkAlt aria-hidden="true" />
                </button>
              </div>
            </section>
          </div>

          {/* Session */}
          <section className="eb-card adp-session">
            <div className="eb-row">
              <div className="eb-row__main">
                <p className="adp-session__title">Session</p>
                <p className="adp-session__hint">Se déconnecter de ce compte sur cet appareil.</p>
              </div>
              <div className="eb-row__actions logout-container">
                <button className="logout-button" onClick={handleLogout}>
                  <FaSignOutAlt aria-hidden="true" /> Déconnexion
                </button>
              </div>
            </div>
          </section>
        </div>

        {/* Pseudo modal */}
        {showModal && (
          <div className="adp-modal-overlay" role="dialog" aria-modal="true">
            <div className="adp-modal">
              <h2 className="adp-modal__title">Définir un nom d'entreprise</h2>
              <input
                type="text"
                className="eb-input"
                value={newPseudo}
                onChange={(e) => setNewPseudo(e.target.value)}
                placeholder="Votre nom d'entreprise"
              />
              <div className="adp-modal__actions">
                <button className="pseudo-save-button eb-btn eb-btn--primary" onClick={handleSavePseudo}>
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProfil;
